import {
  Component,
  OnDestroy,
  OnInit,
  HostBinding,
  HostListener,
  ViewEncapsulation,
  ViewChild,
  inject,
} from "@angular/core";
import * as _ from "lodash";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { AuthenticationService } from "@core/services/auth";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreMediaService } from "@core/services/media.service";

import { User } from "@core/models";

import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Location } from "@angular/common";

import { PaymentService } from "../../../../@core/services/payment.service";
import Swal from "sweetalert2";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Plan } from "../../../main/pages/payment-page/components/provide-subscription-plan/provide-subscription-plan.component";
import { paymentStages } from "../../../main/pages/payment-page/upgrade-page.component";
import { ToastrService } from "ngx-toastr";
import { DashboardModuleService } from "app/main/dashboards/dashboard-module.service";
import { SubscriptionService } from "@core/services/subscription.service";
import { BreakpointObserver } from "@angular/cdk/layout";
import { serverErrorMessage } from "@core/helpers";
import { HelperFunctionsService } from "@core/services/helper-functions.service";
import {
  TeacherToolsViewMode,
  TeacherToolsViewModeService,
} from "app/main/cbc/teacher-tools/services/view-mode.service";

const TEACHER_TOOLS_ROLE_LABELS: Record<TeacherToolsViewMode, string> = {
  ADMIN: "Admin",
  HOD: "Head of Department",
  TEACHER: "Teacher",
};

@Component({
  standalone: false,
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<boolean>();

  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: User;
  public isStudent = false;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;
  hideNightVision = true;

  isLoadingPlans = false;
  plans: Plan[] = [];
  paymentProcessStage!: number;
  freemiumStatus: any;
  currentUrl: string;
  pageTitle: string = '';
  isMawinguUser: boolean = false;
  isSubscribedUser: boolean = false;

  @HostBinding("class.fixed-top")
  public isFixed = false;

  @HostBinding("class.navbar-static-style-on-scroll")
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener("window:scroll", [])
  onWindowScroll() {
    if (
      (window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == "navbar-static-top" &&
      this.coreConfig.layout.type == "horizontal"
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<boolean>;
  private routerSubscription: Subscription;
  private _helpers = inject(HelperFunctionsService);
  private _teacherToolsViewMode = inject(TeacherToolsViewModeService);

  showTimer = false;
  countdownValue = 300;
  countdownSubscription: Subscription;
  showTimerSubscription: Subscription;
  planName!: string;
  largeScreen: boolean;
  isLargeScreen: boolean;

  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    public _translateService: TranslateService,
    private paymentService: PaymentService,
    private _modalService: NgbModal,
    private _toastr: ToastrService,
    private modalService: NgbModal,
    private location: Location,
    private dashboardService: DashboardModuleService,
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute,
    private breakpointObserver: BreakpointObserver
  ) {
    this.currentUser = this._authenticationService.currentUserValue;

    if (this.currentUser) {
      this.isStudent =
        this.currentUser.role === 30 || this.currentUser.role === 31;
    }

    this.currentUrl = window.location.href;

    if (this.currentUrl) this.getPageTitle(this.currentUrl);

     // Subscribe to router changes to update the title dynamically
     this.routerSubscription = this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = window.location.href;
        if (this.currentUrl) this.getPageTitle(this.currentUrl);
      }
    });

    // this.dashboardService.startCountdown();
    // this.dashboardService.updateWeeklyFreemium(
    //   Number(localStorage.getItem("countDownValue"))
    // );

    this.countdownSubscription = this.dashboardService.countdown$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.countdownValue = value;
      });
    this.showTimerSubscription = this.dashboardService.showTimer$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.showTimer = value;
      });
    if (this.currentUser) {
      this.showTimerSubscription = this.dashboardService.recallFreemiumStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.loadFreemiumStatus();
        });
    }

    this.languageOptions = {
      en: {
        title: "English",
        flag: "us",
      },
      fr: {
        title: "French",
        flag: "fr",
      },
      de: {
        title: "German",
        flag: "de",
      },
      pt: {
        title: "Portuguese",
        flag: "pt",
      },
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);

    this._coreConfigService.setConfig(
      { app: { appLanguage: language } },
      { emitEvent: true }
    );
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem("prevSkin");

    if (this.currentSkin === "dark") {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : "default" } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem("prevSkin", this.currentSkin);
      this._coreConfigService.setConfig(
        { layout: { skin: "dark" } },
        { emitEvent: true }
      );
    }
  }

  /**
   * Logout method
   */
  logout() {
    this._authenticationService.logout();
    // this._router.navigate(['login']);
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.onEvt();
    // Subscribe to the config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
        this.horizontalMenu = config.layout.type === "horizontal";
        this.hiddenMenu = config.layout.menu.hidden === true;
        this.currentSkin = config.layout.skin;

        // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
        if (this.coreConfig.layout.type === "vertical") {
          setTimeout(() => {
            if (this.coreConfig.layout.navbar.type === "fixed-top") {
              this.isFixed = true;
            }
          }, 0);
        }
      });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == "horizontal") {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          const isFixedTop = this.breakpointObserver.isMatched('(min-width: 1200px)');
          if (isFixedTop) {
            this.isFixed = false;
          } else {
            this.isFixed = true;
          }
        });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang,
    });

    // share url
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = window.location.href;
        console.log(this.currentUrl)
      }
    });

    // check for token id in the route meaning it's payment link clicked on mobile or a non-loggedin user wants to join a contest
    if(window.location.href.includes("token") || window.location.href.includes("contestCode") || window.location.href.includes("bee-wiz") || this.currentUrl.includes('tryout')|| this.currentUrl.includes('request-exams')) return;
    // Anonymous visitors have no subscription to fetch, and asking anyway answers 401. That sends
    // the error interceptor into a refresh it cannot perform, which logs out and rewrites the URL
    // with a returnUrl — on the login page, a returnUrl pointing back at the login page.
    if (this.currentUser?.access_token) this.checkUserSubscription();
    this.checkScreenSize();
    this.checkIsMawinguUser();
  }

  checkIsMawinguUser() {
    if(this.currentUser && this.currentUser.mawinguAccount ) {
      this.isMawinguUser = true;
    }
  }

  checkIsSubscribedUser() {
    if(this.currentUser && this.currentUser.subscription && this.currentUser.subscription.subscribed ) {
      this.isSubscribedUser = true;
    }
  }

  openUpgradeModal() {
    if (!this.currentUser?.subscription?.subscribed) {
      this.loadUpgradePage();
    }
  }

  get showContentForm(): boolean {
    const baseUrl = this._router.url.split('?')[0];
    return baseUrl === "/dashboard/student" || baseUrl === "/dashboard/video-player";
  }

  checkScreenSize(): void {
    const layoutChanges = this.breakpointObserver.observe([
      "(min-width: 768px)",
      "(min-width: 1200px)",
    ]);

    layoutChanges.subscribe((result) => {
      this.largeScreen = result.matches;
      this.isLargeScreen = result.breakpoints['(min-width: 1200px)'];
    });
  }

  checkUserSubscription() {
    this.subscriptionService
      .getUserSubscriptionDetails()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (res) => {
          this.paymentService.currentUserSubscription.next(res);
          if (res && res?.subscription?.subscribed) {
            this.currentUser.subscription.subscribed =
              res.subscription.subscribed;
          }

          if ((res && !res.privacyPolicyStatus) || !res.termsOfServiceStatus) {
            this.paymentService.updateTermsOfServiceStatus().subscribe({
              error: () => {
                // An automatic sync of the terms status; the user didn't ask for it, so nothing to show.
              },
            });
          }
        },
        error: (err) => {
          console.log("error>>",err)
        },
      });
  }

  promptPrivacyPolicyAndTOS() {
    Swal.fire({
      title: "Updates to our Terms of Service",
      html: `
            <p>Hello 👋, we have made some updates to our
             <a href="https://zerakitermsofservice.blogspot.com/2023/09/zeraki-learning-application-terms-and.html" target="_blank" class="text-underline text-primary">Terms of Service</a>
              and
              <a href="https://zerakiprivacypolicy.blogspot.com/2023/09/zeraki-products-privacy-policy.html" target="_blank" class="text-underline text-primary">Privacy Policy</a>
            </p>
            <p>
                By proceeding, you agree to the Terms of Service and Privacy Policy.
            </p>
            `,
      confirmButtonText: "Ok",
      customClass: {
        confirmButton: "btn btn-primary btn-block waves-effect waves-light",
        htmlContainer: "text-left",
      },
      allowOutsideClick: false,
      focusConfirm: true,
    }).then((confirmed) => {
      if (confirmed.isConfirmed) {
        this.updateTermsOfServiceStatus();
      }
    });
  }

  updateTermsOfServiceStatus(): void {
    this.paymentService.updateTermsOfServiceStatus().subscribe({
      error: (err) => {
        this._helpers.showWarningToastr(
          serverErrorMessage(err, "Couldn't accept the Terms of Service. Please try again.")
        );
      },
    });
  }

  

  modalOpenVC(modalVC) {
    this.modalService.open(modalVC, {
      centered: true,
      size: "lg",
    });
  }

  shareUrl() {
    this.modalService.dismissAll();
    navigator.clipboard.writeText(this.currentUrl).then(() => {
      // The link is copied whatever the server says; the request only counts the share.
      this._toastr.success("Link copied successfully!", "Success");
      this.dashboardService.shareZerakiLearning("Web").subscribe({
        error: () => {
          // Counting the share is bookkeeping; the user already has their link.
        },
      });
    });
  }

  @ViewChild("upgradeModal") upgradeModal: any;

  public loadUpgradePage() {
    this._modalService.open(this.upgradeModal, {
      windowClass: "modal",
      centered: true,
      size: "lg",
      backdrop: true,
      keyboard: false,
    });
    // this.paymentService.initiatePaymentModule(this.upgradeModal);
  }

  formatTime(timeInSeconds: number) {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")} mins`;
  }

  loadFreemiumStatus() {
    // const timerRunning = localStorage.getItem("timerRunning");
    // if (!(timerRunning != undefined && timerRunning != null)) {
    //   this.paymentService
    //     .getFreemiumStatus()
    //     .pipe(takeUntil(this.destroy$))
    //     .subscribe((respData) => {
    //       if (respData) {
    //         this.freemiumStatus = respData;
    //         this.dashboardService.setFreemiumStatus(respData);
    //       }
    //     });
    // }
  }

  loadPaymentPlans() {
    this.isLoadingPlans = true;
    this.paymentService.getPlans().subscribe(
      (data) => {
        this.plans = ((data as any)?.plans ?? data ?? []) as Plan[];
        this.isLoadingPlans = false;
      },
      (error) => {
        this.isLoadingPlans = false;
      }
    );
  }

  processStage(stage: any) {
    this.paymentProcessStage = stage;
  }

  cancelPaymentProcess() {
    if (this.paymentProcessStage == paymentStages.DISPLAY_PAYMENT_SUCCESS)
      window.location.reload();
    this._modalService.dismissAll();
  }

  onEvt() {
    this.paymentService.paymentEvent.subscribe((planName: string) => {
      this.planName = planName;
      this.loadUpgradePage();
    });
  }

  getPageTitle(currentUrl: string): void {
    const hashValue = currentUrl.split('#/')[1];
    if (hashValue) {
      const firstSegment = hashValue.split('/')[0];
      this.pageTitle = firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
    }
  }

  get schoolName(): string {
    return this.currentUser?.currentSchool?.name?.trim() || '';
  }

  /** In Teacher Tools the role in use is the surface on screen, so a head of department reads as one. */
  get userRoleLabel(): string {
    const surface = this.currentUrl?.includes('#/cbc/teacher-tools') ? this._teacherToolsViewMode.inUse : null;
    if (surface) {
      return TEACHER_TOOLS_ROLE_LABELS[surface];
    }
    const role = this.currentUser?.userRole;
    if (role === 200) return 'Customer Support';
    if (role === 100) return 'Super Admin';
    if (role === 60) return 'Parent';
    if (role === 50) return 'Admin';
    if (role === 40 || role === 41) return 'Teacher';
    if (role === 32) return 'Student';
    return 'Student';
  }

  get languageLabel(): string {
    const key =
      (typeof this.selectedLanguage === 'string' ? this.selectedLanguage : null) ||
      this._translateService.currentLang ||
      'en';
    return this.languageOptions?.[key]?.title || 'English';
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();

    this.countdownSubscription.unsubscribe();
    this.showTimerSubscription.unsubscribe();
    this.dashboardService.stopCountdown();
  }
}
